import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import * as yup from 'yup';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import { IconCirclePlus, IconInfoSquareRounded } from '@rossum/ui/icons/tabler';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import ValidationInput from '../../../../components/ReactHookForm/ValidationInput';
import { emailDomainName } from '../../../../constants/config';
import { clearValidationsOnChangeCreator } from '../../../../lib/messages';
import { clearValidationMessagesAction } from '../../../../redux/modules/validationMessages/action';
import { InputMessages } from '../../../../redux/modules/validationMessages/types';

const createInboxValidationSchema = (intl: IntlShape) =>
  yup.object().shape({
    emailPrefix: yup
      .string()
      .required(
        intl.formatMessage({
          id: 'containers.settings.queues.email.messages.emailPrefix.required',
        })
      )
      .matches(
        /^\S*$/,
        intl.formatMessage({
          id: 'containers.settings.queues.email.messages.emailPrefix.noSpaces',
        })
      ),
  });

type Props = {
  clearValidationMessages: typeof clearValidationMessagesAction;
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (_emailPrefixAttrubutes: { emailPrefix: string }) => void;
  validationMessages: InputMessages;
};

const CreateInboxModal = ({
  clearValidationMessages,
  isOpen,
  onCancel,
  onSubmit,
  validationMessages,
}: Props) => {
  const intl = useIntl();

  const { reset, handleSubmit, control } = useForm({
    mode: 'onTouched',
    defaultValues: {
      emailPrefix: '',
    },
    resolver: yupResolver(createInboxValidationSchema(intl)),
  });

  useEffect(() => {
    if (!isOpen) {
      reset({ emailPrefix: '' });
    }
  }, [reset, isOpen]);

  const clearValidationsOnChange = clearValidationsOnChangeCreator(
    clearValidationMessages,
    'inbox',
    validationMessages
  );

  return (
    <Dialog
      component="form"
      open={isOpen}
      onClose={onCancel}
      onSubmit={handleSubmit(onSubmit)}
    >
      <DialogTitle
        title={intl.formatMessage({
          id: 'containers.settings.queues.email.noInbox.create',
        })}
        onClose={onCancel}
      />
      <DialogContent>
        <Stack pt={2} spacing={1}>
          <Stack direction="row" gap={0.5}>
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'containers.settings.queues.email.emailPrefix',
              })}
            </Typography>
            <Tooltip
              title={intl.formatMessage(
                {
                  id: 'containers.settings.queues.email.emailPrefix.tooltip',
                },
                { domainName: emailDomainName }
              )}
            >
              <SvgIcon fontSize="medium" color="action">
                <IconInfoSquareRounded />
              </SvgIcon>
            </Tooltip>
          </Stack>
          <ValidationInput
            clearValidationsOnChange={clearValidationsOnChange('emailPrefix')}
            placeholder={intl.formatMessage({
              id: 'containers.settings.queues.email.emailPrefix.placeholder',
            })}
            control={control}
            getErrorMessage={(_, { message }) => message ?? ''}
            name="emailPrefix"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row-reverse"
          spacing={2}
          justifyContent="flex-start"
          sx={{ m: 2, mt: 0 }}
        >
          <Button
            variant="contained"
            startIcon={
              <SvgIcon fontSize="large">
                <IconCirclePlus />
              </SvgIcon>
            }
            type="submit"
          >
            {intl.formatMessage({
              id: 'containers.settings.queues.email.noInbox.create',
            })}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => onCancel()}
          >
            {intl.formatMessage({ id: 'components.modal.cancel' })}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInboxModal;
